'use client'
import type { ReactNode } from 'react'

import { cn } from '@morpho-org/ui-v6/utils'
import { Tooltip, TooltipContent, TooltipTrigger } from '@repo/ui'

import { Popover, PopoverContent, PopoverTrigger } from '@/components/common/overlays'
import { useIsTouchDevice } from '@/hooks/useBreakpoints'

// The rule belongs to this app, not to Blue, which permits both sides at once. Each string
// names the tab that holds the exit, and the lend side names both legs of a borrow position
// because the gate fires on collateral alone too.
export const DISABLED_BORROW_TOOLTIP =
  'This app supports a position on one side of a market at a time. To borrow, withdraw your lend position from the Withdraw tab first.'
export const DISABLED_LEND_TOOLTIP =
  'This app supports a position on one side of a market at a time. To lend, repay any outstanding loan and withdraw your collateral from the Repay tab.'

/**
 * Wrapper for a disabled control: it takes the hover the control swallows and the clicks it
 * cannot, which React still propagates to an ancestor row link across a portal. Shared by the
 * market panel's tabs and the portfolio lend menu so the one-sided rule reads the same on both.
 */
export const DisabledActionTooltip = ({
  children,
  disabled,
  message,
  className = 'inline-flex'
}: {
  children: ReactNode
  disabled: boolean
  /** Omitted while the reason is still unknown: a disabled control beats a tooltip that guesses. */
  message?: string
  className?: string
}) => {
  const isTouchDevice = useIsTouchDevice()

  if (!disabled) return children

  const wrapper = (
    <span
      aria-label={message}
      // On touch the wrapper is the tap target, and a disabled control swallows the tap
      // unless its own pointer events are off.
      className={cn(className, isTouchDevice && '[&>*]:pointer-events-none')}
      // Focusable only when it has something to say; an unnamed tab stop helps nobody.
      tabIndex={message ? 0 : undefined}
      role={message && isTouchDevice ? 'button' : undefined}
      onClick={event => event.stopPropagation()}
      // A span synthesises no click from Enter or Space, so a keyboard user on a touch-capable
      // laptop could focus the popover trigger without ever opening it.
      onKeyDown={
        message && isTouchDevice
          ? event => {
              if (event.key !== 'Enter' && event.key !== ' ') return
              event.preventDefault()
              event.currentTarget.click()
            }
          : undefined
      }
    >
      {children}
    </span>
  )

  if (!message) return wrapper

  // Radix tooltips are hover/focus only, so on touch the reason opens on tap instead
  // (same Popover fallback as VaultPendingTimelockInfo).
  if (isTouchDevice) {
    return (
      <Popover>
        <PopoverTrigger asChild>{wrapper}</PopoverTrigger>
        <PopoverContent side="top" sideOffset={8}>
          {message}
        </PopoverContent>
      </Popover>
    )
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>{wrapper}</TooltipTrigger>
      <TooltipContent>{message}</TooltipContent>
    </Tooltip>
  )
}
